import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { canSeeNav, permForPath, ROLE_LABEL } from "../lib/navigation";
import { PERMISSION_INFO } from "../lib/permissions";
import { useRights } from "../lib/useRights";

/**
 * Garde des routes (palier 2 de l'audit, admin V1.30) : une page que le menu
 * cache à un compte ne s'ouvre pas non plus par son adresse (signet, lien
 * copié). Même règle que le menu (lib/navigation.ts) : le droit de l'entrée
 * (V1.51, Gestion des droits). Le serveur reste la vraie barrière ; ceci
 * remplace seulement une page pleine d'erreurs 403 par un message clair.
 */
export function RouteGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { role, perms } = useRights();
  const perm = permForPath(pathname);

  if (!perm) return <>{children}</>;
  // Rôle pas encore connu : on attend plutôt que d'afficher un refus à tort.
  if (!role) return null;
  if (canSeeNav({ perm }, perms)) return <>{children}</>;

  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <div className="rounded-xl border border-[color:var(--color-alto-border)] bg-white p-6">
        <p className="text-xs uppercase tracking-wider text-[color:var(--color-alto-muted)]">Accès réservé</p>
        <h1 className="mt-1 text-xl font-semibold">Cette page n'est pas ouverte à votre rôle</h1>
        <p className="mt-3 text-sm">
          Elle demande le droit <strong>« {PERMISSION_INFO[perm].label} »</strong>, que le rôle{" "}
          <strong>{ROLE_LABEL[role]}</strong> n'a pas.
        </p>
        <p className="mt-2 text-sm text-[color:var(--color-alto-muted)]">
          Si vous en avez besoin, demandez à un administrateur de modifier votre rôle (Administration → Utilisateurs).
        </p>
        <Link to="/" className="mt-4 inline-block text-sm text-[color:var(--color-alto-accent)] hover:underline">
          ← Retour au tableau de bord
        </Link>
      </div>
    </div>
  );
}
